import type { PosProduct } from './api';

/**
 * The till's category chips, derived from the catalog already in hand:
 * `GET /pos/products` carries each product's category, so there is nothing to
 * fetch. Pure, like `pos-search.ts`.
 */

/** Chip id for "every product". */
export const CHIP_ALL = 'all';
/** Chip id for products with no category. */
export const CHIP_OTHER = 'other';

export type CategoryChip = { id: string; label: string; count: number };

type Categorised = Pick<PosProduct, 'categoryId' | 'categoryName' | 'categorySortOrder'>;

/**
 * The chips to offer: All, then each category in the admin's order (ties by
 * name), then Other when some products have no category.
 *
 * A catalog with no categories at all returns NO chips: a lone "All" is a dead
 * control, and most existing tenants have never categorised a product.
 */
export function categoryChips(products: Categorised[]): CategoryChip[] {
  const byId = new Map<string, { label: string; sort: number; count: number }>();
  let uncategorised = 0;
  for (const p of products) {
    if (!p.categoryId) {
      uncategorised += 1;
      continue;
    }
    const seen = byId.get(p.categoryId);
    if (seen) seen.count += 1;
    else {
      byId.set(p.categoryId, {
        label: p.categoryName ?? 'Unnamed',
        sort: p.categorySortOrder ?? 0,
        count: 1,
      });
    }
  }
  if (byId.size === 0) return [];

  const categories = [...byId.entries()]
    .sort(([, a], [, b]) => a.sort - b.sort || a.label.localeCompare(b.label))
    .map(([id, c]) => ({ id, label: c.label, count: c.count }));
  return [
    { id: CHIP_ALL, label: 'All', count: products.length },
    ...categories,
    ...(uncategorised > 0 ? [{ id: CHIP_OTHER, label: 'Other', count: uncategorised }] : []),
  ];
}

/**
 * The chip to treat as selected. A chip that is no longer offered (the branch
 * changed, or its last product was archived) falls back to All rather than
 * leaving the grid empty behind a chip nobody can see.
 */
export function activeChip(chips: CategoryChip[], selected: string): string {
  return chips.some((c) => c.id === selected) ? selected : CHIP_ALL;
}

export function filterByChip<T extends Categorised>(products: T[], chipId: string): T[] {
  if (chipId === CHIP_ALL) return products;
  if (chipId === CHIP_OTHER) return products.filter((p) => !p.categoryId);
  return products.filter((p) => p.categoryId === chipId);
}
